import { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { pickerBlockedMessage, watchPickerLoad } from '../utils/pickerLoadWatch';

const PICKER_BLOCKED_TOAST = 'google-picker-blocked';

declare global {
  interface Window {
    gapi: any;
    google: any;
  }
}

export interface GoogleDriveFile {
  id: string;
  name: string;
  mimeType: string;
  sizeBytes?: number;
}

export function useGoogleDrivePicker() {
  const [isLoaded, setIsLoaded] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    // Load Google API script
    const loadGoogleAPI = () => {
      if (window.gapi && window.google) {
        setIsLoaded(true);
        return;
      }

      const script1 = document.createElement('script');
      script1.src = 'https://apis.google.com/js/api.js';
      script1.async = true;
      script1.defer = true;
      script1.onload = () => {
        window.gapi.load('picker', () => {
          setIsLoaded(true);
        });
      };
      document.body.appendChild(script1);
    };

    loadGoogleAPI();
  }, []);

  const openPicker = useCallback(
    async (
      apiKey: string,
      accessToken: string,
      onSelect: (files: GoogleDriveFile[]) => void,
      mimeTypes?: string[],
      title?: string
    ) => {
      if (!isLoaded || !window.google || !window.gapi) {
        console.error('Google Picker API not loaded');
        return;
      }

      if (!apiKey) {
        console.error('Google API key not provided');
        return;
      }

      if (!accessToken) {
        console.error('Google OAuth access token not provided');
        return;
      }

      console.log('Opening Google Picker with:', {
        apiKeyPresent: !!apiKey,
        accessTokenPresent: !!accessToken,
        accessTokenPrefix: accessToken.substring(0, 20) + '...',
        mimeTypeFilter: mimeTypes,
      });

      setIsLoading(true);

      // No report from the picker means it's stuck on Google's sign-in page (third-party cookies
      // blocked for this site) - say so, instead of leaving the user at a dead end
      const watch = watchPickerLoad(
        () => {
          setIsLoading(false);
          toast.warn(pickerBlockedMessage(window.location.host), { autoClose: false, toastId: PICKER_BLOCKED_TOAST });
        },
        () => toast.dismiss(PICKER_BLOCKED_TOAST)
      );

      try {
        const view = new window.google.picker.DocsView(window.google.picker.ViewId.DOCS).setIncludeFolders(true);

        // Apply MIME type filter if provided
        if (mimeTypes && mimeTypes.length > 0) {
          view.setMimeTypes(mimeTypes.join(','));
        }

        const picker = new window.google.picker.PickerBuilder()
          .addView(view)
          .setOAuthToken(accessToken)
          .setDeveloperKey(apiKey)
          .setOrigin(window.location.protocol + '//' + window.location.host)
          .enableFeature(window.google.picker.Feature.SUPPORT_DRIVES)
          .setTitle(title || 'Select files from Google Drive')
          .setCallback((data: any) => {
            console.log('Picker callback received:', data);
            watch.onAction();

            // Check for errors
            if (data[window.google.picker.Response.ERROR]) {
              console.error('Picker error:', data[window.google.picker.Response.ERROR]);
              setIsLoading(false);
              return;
            }

            if (data.action === window.google.picker.Action.PICKED) {
              const files: GoogleDriveFile[] = data.docs.map((doc: any) => ({
                id: doc.id,
                name: doc.name,
                mimeType: doc.mimeType,
                sizeBytes: doc.sizeBytes,
              }));
              console.log('Files selected:', files);
              onSelect(files);
            } else if (data.action === window.google.picker.Action.CANCEL) {
              console.log('Picker cancelled');
            }
            setIsLoading(false);
          })
          .build();

        picker.setVisible(true);
      } catch (error) {
        console.error('Error opening Google Drive Picker:', error);
        watch.stop();
        setIsLoading(false);
      }
    },
    [isLoaded]
  );

  return { openPicker, isLoaded, isLoading };
}
